import React, { useCallback } from 'react';
import { useTweetContentStore } from '../../stores/tweetContent/tweetContentStore';
import { ContentType } from '../../api/bip-api/types';
import useEscapeKeyClose from '../../hooks/useEscapeKeyClose';
import TweetList from './TweetList';
import TweetCreateForm from './TweetCreateForm';
import TweetDetail from './TweetDetail';

const TweetManagementModal: React.FC = () => {
  const {
    isModalOpen,
    modalTokenAddress,
    modalTokenSymbol,
    tweets,
    selectedTweetId,
    viewMode,
    isLoading,
    isGenerating,
    isUpdating,
    isPublishing,
    closeModal,
    generateTweet,
    updateTweet,
    publishTweet,
    selectTweet,
    setViewMode,
  } = useTweetContentStore();

  useEscapeKeyClose(isModalOpen, closeModal);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        closeModal();
      }
    },
    [closeModal]
  );

  const handleCreateNew = useCallback(() => {
    setViewMode('create');
  }, [setViewMode]);

  const handleSelectTweet = useCallback(
    (id: string) => {
      selectTweet(id);
    },
    [selectTweet]
  );

  const handleGenerate = useCallback(
    async (contentType: ContentType) => {
      if (modalTokenAddress) {
        await generateTweet(modalTokenAddress, contentType);
      }
    },
    [modalTokenAddress, generateTweet]
  );

  const handleCancelCreate = useCallback(() => {
    setViewMode('list');
  }, [setViewMode]);

  const handleSave = useCallback(
    async (id: string, contents: string[]) => {
      await updateTweet(id, contents);
    },
    [updateTweet]
  );

  const handlePublish = useCallback(
    async (id: string, isPersonalTweet: boolean = false) => {
      await publishTweet(id, isPersonalTweet);
    },
    [publishTweet]
  );

  const selectedTweet = tweets.find((t) => t.id === selectedTweetId);

  if (!isModalOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-900 rounded-xl max-w-[1792px] w-full mx-4 h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">
            [{modalTokenSymbol}] Tweet Management
          </h2>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body - Split Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Tweet List */}
          <div className="w-1/3 border-r border-gray-700 p-4 overflow-hidden">
            <TweetList
              tweets={tweets}
              selectedTweetId={selectedTweetId}
              isLoading={isLoading}
              onSelectTweet={handleSelectTweet}
              onCreateNew={handleCreateNew}
            />
          </div>

          {/* Right Panel - Detail / Create Form */}
          <div className="w-2/3 p-4 overflow-hidden">
            {viewMode === 'create' ? (
              <TweetCreateForm
                isGenerating={isGenerating}
                onGenerate={handleGenerate}
                onCancel={handleCancelCreate}
              />
            ) : viewMode === 'detail' && selectedTweet ? (
              <TweetDetail
                tweet={selectedTweet}
                isUpdating={isUpdating}
                isPublishing={isPublishing}
                onSave={handleSave}
                onPublish={handlePublish}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  <p className="text-sm">
                    Select a tweet to view details or create a new one.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TweetManagementModal;
